<template>
  <header
    class="header w-full py-4 px-6 bg-transparent fixed top-0 left-0 z-[1000]"
    :class="{ scrolled: isScrolled }"
  >
    <div class="mobile-alignment-wrapper">
      <div class="header-container">
        <!-- Logo Section -->
        <div class="logo-section flex items-center gap-2">
          <picture>
            <source srcset="/full-icon.webp" type="image/webp" />
            <img 
              src="/full-icon.png" 
              alt="ElevatED Logo" 
              class="logo-image h-[120px] w-auto object-contain" 
              width="120" 
              height="120" 
              loading="eager" 
              fetchpriority="high"
            />
          </picture>
        </div>

        <!-- Navigation Links -->
        <nav class="navigation flex items-center gap-8 ml-auto mr-6">
          <router-link
            to="/"
            class="nav-link text-[18px] font-medium text-[#666666] no-underline py-2 relative transition-all duration-200"
            active-class="active"
            >Home</router-link
          >
          <router-link
            to="/about"
            class="nav-link text-[18px] font-medium text-[#666666] no-underline py-2 relative transition-all duration-200"
            active-class="active"
            >About</router-link
          >
        </nav>

        <!-- Action Buttons -->
        <div class="action-buttons flex items-center gap-3">
          <button
            class="btn btn-register py-[10px] px-[35px] rounded-[24px] text-[18px] font-bold font-sans cursor-pointer transition-all duration-200 border-0 outline-none bg-white text-black border-[3px] border-black hover:bg-[#FCBB02] hover:text-black hover:border-black"
            :class="{ active: isRegisterRoute }"
            @click="navigateToRegisterRole"
          >
            Register
          </button>
          <button
            class="btn btn-login py-[10px] px-[35px] rounded-[24px] text-[18px] font-bold font-sans cursor-pointer transition-all duration-200 border-[3px] border-black bg-black text-white hover:bg-[#FCBB02] hover:text-black hover:border-black"
            :class="{ active: isLoginRoute }"
            @click="navigateToLogin"
          >
            Log In
          </button>
        </div>

        <!-- Mobile Hamburger Menu -->
        <button
          class="mobile-menu-toggle hidden flex-col justify-around w-[25px] h-[25px] bg-transparent border-0 cursor-pointer p-0"
          @click="toggleMobileMenu"
          aria-label="Toggle mobile menu"
          :aria-expanded="isMobileMenuOpen"
        >
          <span
            class="hamburger-line w-[25px] h-[3px] bg-black rounded transition-all duration-300"
          ></span>
          <span
            class="hamburger-line w-[25px] h-[3px] bg-black rounded transition-all duration-300"
          ></span>
          <span
            class="hamburger-line w-[25px] h-[3px] bg-black rounded transition-all duration-300"
          ></span>
        </button>
      </div>

      <!-- Mobile Navigation Menu -->
      <div
        class="mobile-nav hidden absolute top-full left-0 right-0 bg-white border-[3px] border-black rounded-b-[50px] shadow-[8px_8px_0_#000] z-[999]"
        :class="{ active: isMobileMenuOpen }"
      >
        <div class="mobile-nav-content p-5 flex flex-col gap-[15px]">
          <router-link
            to="/"
            class="mobile-nav-link text-[18px] font-medium text-[#666666] no-underline py-3 border-b-2 border-[#f0f0f0] transition-all duration-300"
            @click="closeMobileMenu"
            >Home</router-link
          >
          <router-link
            to="/about"
            class="mobile-nav-link text-[18px] font-medium text-[#666666] no-underline py-3 border-b-2 border-[#f0f0f0] transition-all duration-300"
            @click="closeMobileMenu"
            >About</router-link
          >
          <router-link
            :to="{ name: 'register-role' }"
            class="mobile-nav-link text-[18px] font-medium text-[#666666] no-underline py-3 border-b-2 border-[#f0f0f0] transition-all duration-300"
            @click="closeMobileMenu"
            >Register</router-link
          >
          <router-link
            :to="{ name: 'login' }"
            class="mobile-nav-link text-[18px] font-medium text-[#666666] no-underline py-3"
            @click="closeMobileMenu"
            >Login</router-link
          >
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import "@/assets/main.css";

// Router
const router = useRouter();
const route = useRoute();

// Mobile menu state
const isMobileMenuOpen = ref(false);

// Scroll state
const isScrolled = ref(false);

// Add scroll event listener
const handleScroll = () => {
  isScrolled.value = window.scrollY > 10;
};

// Set up and clean up scroll event listener
onMounted(() => {
  window.addEventListener("scroll", handleScroll, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});

// Check if current route is register-related
const isRegisterRoute = computed(() => {
  const path = route.path.toLowerCase();
  return (
    path.includes("/student-register") ||
    path.includes("/tutor-register") ||
    path.includes("/register") ||
    path.includes("register") ||
    path.includes("/email-verification")
  );
});

// Check if current route is login-related
const isLoginRoute = computed(() => {
  const path = route.path.toLowerCase();
  return path.includes("/login") || path.includes("login");
});

// Navigation functions
const navigateToRegisterRole = () => {
  router.push({ name: "register-role" });
  closeMobileMenu();
};


const navigateToLogin = () => {
  router.push({ name: "login" });
  closeMobileMenu();
};

// Toggle mobile menu
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

// Close mobile menu
const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
};
</script>

<style scoped>
body {
  padding-top: 120px;
}

.header {
  width: 100%;
  padding: 16px 24px;
  background: transparent;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  box-sizing: border-box;
  transform: translateZ(0);
  will-change: transform;
}

/* Mobile Alignment Wrapper - matches buttons-container approach */
.mobile-alignment-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
}

.header-container {
  display: flex;
  align-items: center;
  width: 80em;
  height: 6em;
  margin: 0 auto;
  margin-top: 1.25em;
  background: white;
  border-radius: 50px;
  padding: 12px 24px;
  border: 3px solid #000;
  box-shadow: 8px 8px 0px #000;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

/* Logo Section */
.logo-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.logo-image {
  height: 120px;
  width: auto;
  object-fit: contain;
}

.logo-icon {
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.logo-text {
  font-size: 20px;
  font-weight: 600;
  color: #000;
  letter-spacing: -0.02em;
}

/* Navigation */
.navigation {
  display: flex;
  align-items: center;
  gap: 32px;
  margin-left: auto;
  margin-right: 24px;
}

.nav-link {
  font-size: 18px;
  font-weight: 500;
  color: #666;
  text-decoration: none;
  padding: 8px 0;
  position: relative;
  transition: all 0.2s ease;
}

.nav-link:hover {
  color: #000;
  font-weight: 700;
  background: none !important;
}

.nav-link:hover::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background: #000;
  border-radius: 2px;
}

.nav-link.active {
  font-weight: 700;
  color: #000;
}

.nav-link.active::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 5px;
  background: #000;
  border-radius: 10px;
}

/* Ensure no background colors on any nav-link states */
.nav-link,
.nav-link:hover,
.nav-link:focus,
.nav-link:active,
.nav-link.active {
  background: transparent !important;
  background-color: transparent !important;
}

/* Mobile Hamburger Menu */
.mobile-menu-toggle {
  display: none;
  flex-direction: column;
  justify-content: space-around;
  width: 25px;
  height: 25px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
}

.hamburger-line {
  width: 25px;
  height: 3px;
  background: #000;
  border-radius: 2px;
  transition: all 0.3s ease;
}

/* Mobile Navigation */
.mobile-nav {
  display: none;
  position: absolute;
  top: calc(100% + 8px);
  left: 28px;
  right: 28px;
  background: white;
  border: 3px solid #000;
  border-radius: 30px;
  box-shadow: 8px 8px 0px #000;
  z-index: 999;
  margin-top: 0;
}

.mobile-nav.active {
  display: block;
}

.mobile-nav-content {
  padding: 25px 20px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.mobile-nav-link {
  font-size: 18px;
  font-weight: 500;
  color: #666;
  text-decoration: none;
  padding: 12px 0;
  border-bottom: 2px solid #f0f0f0;
  transition: all 0.3s ease;
}

.mobile-nav-link:hover,
.mobile-nav-link.router-link-active {
  color: #000;
  font-weight: 700;
  border-bottom-color: #000;
}

.mobile-nav-link:last-child {
  border-bottom: none;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn {
  padding: 10px 35px;
  border-radius: 24px;
  font-size: 18px;
  font-weight: 700;
  font-family: "Inter", sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;
}

.btn-register {
  background: white;
  color: #000;
  border: 3px solid #000;
}

.btn-register:hover,
.btn-register:active,
.btn-register.active {
  background: #fcbb02;
  color: #000;
  border-color: #000;
}

.btn-login {
  background: #000;
  color: white;
  border: 3px solid #000;
}

.btn-login:hover,
.btn-login:active,
.btn-login.active {
  background: #fcbb02;
  color: #000;
  border-color: #000;
}

/* Blur Effect for Header - Always active for better performance */
.header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  background-color: rgba(255, 255, 255, 0.1);
  z-index: -1;
}

/* Optional: Add subtle opacity change on scroll for visual feedback */
.header.scrolled::before {
  background-color: rgba(255, 255, 255, 0.15);
}

/* Responsive Design */
@media (max-width: 1200px) {
  .header-container {
    width: 90%;
  }
}

/* Tablet Alignment - iPad Mini & Small Tablets */
@media (max-width: 820px) and (min-width: 481px) {
  .mobile-alignment-wrapper {
    width: 90%;
    max-width: 650px;
    margin: 0 auto;
  }

  .header {
    padding: 0;
    justify-content: flex-start;
  }

  .header-container {
    width: 100%;
    max-width: none;
    height: 4.2em;
    padding: 8px 20px;
    margin: 1em auto 0 auto;
    box-shadow: 6px 6px 0px #000;
  }

  .logo-image {
    height: 75px;
  }

  .navigation {
    gap: 16px;
    margin-right: 12px;
  }

  .nav-link {
    font-size: 15px;
  }

  .nav-link:hover::after,
  .nav-link.active::after {
    height: 3px;
    border-radius: 8px;
  }

  .btn {
    padding: 7px 20px;
    font-size: 15px;
  }
}

/* Tablet Alignment - iPad Air */
@media (max-width: 900px) and (min-width: 821px) {
  .mobile-alignment-wrapper {
    width: 92%;
    max-width: 700px;
    margin: 0 auto;
  }

  .header {
    padding: 0;
    justify-content: flex-start;
  }

  .header-container {
    width: 100%;
    max-width: none;
    height: 4.4em;
    padding: 8px 22px;
    margin: 1em auto 0 auto;
    box-shadow: 6px 6px 0px #000;
  }

  .logo-image {
    height: 78px;
  }

  .navigation {
    gap: 18px;
    margin-right: 14px;
  }

  .nav-link {
    font-size: 15.5px;
  }

  .nav-link:hover::after,
  .nav-link.active::after {
    height: 3.5px;
    border-radius: 8px;
  }

  .btn {
    padding: 7px 22px;
    font-size: 15.5px;
  }
}

/* Tablet Alignment - iPad Pro */
@media (max-width: 1024px) and (min-width: 901px) {
  .mobile-alignment-wrapper {
    width: 95%;
    max-width: 750px;
    margin: 0 auto;
  }

  .header {
    padding: 0;
    justify-content: flex-start;
  }

  .header-container {
    width: 100%;
    max-width: none;
    height: 4.6em;
    padding: 8px 24px;
    margin: 1em auto 0 auto;
    box-shadow: 6px 6px 0px #000;
  }

  .logo-image {
    height: 82px;
  }

  .navigation {
    gap: 20px;
    margin-right: 16px;
  }

  .nav-link {
    font-size: 16px;
  }

  .nav-link:hover::after,
  .nav-link.active::after {
    height: 4px;
    border-radius: 8px;
  }

  .btn {
    padding: 8px 24px;
    font-size: 16px;
  }
}

@media (max-width: 600px) {
  .mobile-alignment-wrapper {
    width: 90%;
    max-width: 400px;
    margin: 0 auto;
  }

  .header {
    padding: 0;
    justify-content: flex-start;
  }

  .header-container {
    width: 100%;
    max-width: none;
    height: 3em;
    padding: 6px 12px;
    margin: 0.5em 0 0 0;
    box-shadow: 5px 5px 0px #000;
  }

  .logo-image {
    height: 60px;
  }

  .navigation {
    display: none;
  }

  .action-buttons {
    display: none;
  }

  .mobile-menu-toggle {
    display: flex;
    margin-left: auto;
  }

  .nav-link {
    font-size: 14px;
  }

  .btn {
    padding: 6px 20px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .mobile-alignment-wrapper {
    width: 85%;
    max-width: 350px;
    margin: 0 auto;
  }

  .header {
    padding: 0;
    justify-content: flex-start;
  }

  .header-container {
    width: 100%;
    max-width: none;
    height: 3.5em;
    padding: 8px 15px;
    margin: 0.5em 0 0 0;
    box-shadow: 4px 4px 0px #000;
    justify-content: space-between;
    position: relative;
  }

  .logo-image {
    height: 60px;
  }

  .navigation,
  .action-buttons {
    display: none;
  }

  .mobile-menu-toggle {
    display: flex;
    margin-left: auto;
  }

  .mobile-nav {
    top: calc(100% + 12px);
    left: 10px;
    right: 10px;
    box-shadow: 4px 4px 0px #000;
    border-radius: 25px;
  }
  .mobile-nav-content {
    padding: 20px 15px 15px 15px;
  }

  .mobile-nav-link {
    font-size: 16px;
    padding: 10px 0;
  }
}

@media (max-width: 414px) {
  .mobile-alignment-wrapper {
    width: 85%;
    max-width: 320px;
    margin: 0 auto;
  }

  .header {
    padding: 0;
    justify-content: flex-start;
  }

  .header-container {
    width: 100%;
    max-width: none;
    height: 3em;
    padding: 6px 12px;
    margin: 0.3em 0 0 0;
    box-shadow: 3px 3px 0px #000;
  }

  .logo-image {
    height: 50px;
  }

  .mobile-nav {
    left: 8px;
    right: 8px;
  }
}

@media (max-width: 360px) {
  .mobile-alignment-wrapper {
    width: 80%;
    max-width: 280px;
    margin: 0 auto;
  }

  .header {
    padding: 0;
    justify-content: flex-start;
  }

  .header-container {
    width: 100%;
    max-width: none;
    height: 3em;
    padding: 6px 10px;
    margin: 0.3em 0 0 0;
    box-shadow: 3px 3px 0px #000;
  }

  .logo-image {
    height: 48px;
  }

  .mobile-nav {
    left: 8px;
    right: 8px;
  }
}
</style>
